import { useState, useEffect } from 'react';
import {
  Share2,
  Trash2,
  Pencil,
  Globe,
  Lock,
  Video,
  Play,
  Film
} from 'lucide-react';
import { Card, Badge } from 'react-bootstrap';
import Button from '../UI/button';
import DeleteModal from '../modals/settings/delete-modal';
import EditTripNameImageModal from '../modals/trip/edit-trip-name-image-modal';
import { MakePublicConfirmModal } from '../modals/trip/make-trip-public-modal';
import ShareInviteModal from '../modals/trip/share-invite-modal';
import {
  ITripCardSlide,
  ITripModelSummary
} from '../../Models/trips/trip-summary.model';
import tripsService from '../../services/trips/trips.service';
import { TripImageSlideshow } from './trip-image-slideshow';
import { TripCardDisplayEditor } from './trip-card-display-editor';
import { TripReelView } from './trip-reel-view';

type TripCardProps = {
  trip: ITripModelSummary;
  onClick: () => void;
  onDelete?: () => void;
  onUpdate?: () => void;
};

function capitalizeFirstLetter(str: string): string {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function TripCard({ trip, onClick, onDelete, onUpdate }: TripCardProps) {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showPublicLinkModal, setShowPublicLinkModal] = useState(false);
  const [showMakePublicModal, setShowMakePublicModal] = useState(false);
  const [openShareModalWithSettings, setOpenShareModalWithSettings] =
    useState(false);
  const [deleting, setDeleting] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [isCollaborator, setIsCollaborator] = useState(false);
  //   const [permissionLevel, setPermissionLevel] = useState<
  //     'edit' | 'view' | null
  //   >(null);
  const [sharingMode, setSharingMode] = useState<
    'copy' | 'read' | 'edit' | null
  >(null);
  const [hasCollaborators, setHasCollaborators] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [togglingPrivacy, setTogglingPrivacy] = useState(false);
  const [reelVideoUrl, setReelVideoUrl] = useState<string | null>(null);
  const [showCardEditor, setShowCardEditor] = useState(false);
  const [showReel, setShowReel] = useState(false);

  const permissionLevel = 'edit'; // Placeholder until permissions are implemented
  const formattedDate = trip.createdAt
    ? new Date(trip.createdAt).toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      })
    : '';

  async function handleDelete() {
    setDeleting(true);
    try {
      setShowDeleteModal(false);
      await tripsService.deleteTrip(trip.id);
      if (onDelete) onDelete();
    } catch (error) {
      console.error('Error deleting trip:', error);
      alert('Failed to delete trip. Please try again.');
    } finally {
      setDeleting(false);
    }
  }

  async function handlePrivacyToggle(
    makePublic: boolean,
    settings?: {
      showPublicNotes?: boolean;
      visibleToFollowers?: boolean;
      shareableViaLink?: boolean;
      discoverableInSearch?: boolean;
      defaultShareView?: 'map' | 'photos';
    }
  ) {
    setTogglingPrivacy(true);
    try {
      //   const updates: {
      //     is_public: boolean;
      //     show_public_notes?: boolean;
      //     visible_to_followers?: boolean;
      //     shareable_via_link?: boolean;
      //     discoverable_in_search?: boolean;
      //     default_share_view?: 'map' | 'photos';
      //   } = {
      //     is_public: makePublic
      //   };

      //   if (makePublic && settings) {
      //     if (settings.showPublicNotes !== undefined) {
      //       updates.show_public_notes = settings.showPublicNotes;
      //     }
      //     if (settings.visibleToFollowers !== undefined) {
      //       updates.visible_to_followers = settings.visibleToFollowers;
      //     }
      //     if (settings.shareableViaLink !== undefined) {
      //       updates.shareable_via_link = settings.shareableViaLink;
      //     }
      //     if (settings.discoverableInSearch !== undefined) {
      //       updates.discoverable_in_search = settings.discoverableInSearch;
      //     }
      //     if (settings.defaultShareView !== undefined) {
      //       updates.default_share_view = settings.defaultShareView;
      //     }
      //   }

      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error updating trip privacy:', error);
      alert('Failed to update privacy settings. Please try again.');
    } finally {
      setTogglingPrivacy(false);
    }
  }

  function onPrivacyClick(e: React.MouseEvent) {
    e.stopPropagation();
    e.preventDefault();
    if (trip.isPublic) {
      handlePrivacyToggle(false);
    } else {
      setShowMakePublicModal(true);
    }
  }

  const handleCardClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Only trigger onClick if the click target is the card itself, not a button or interactive element
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('.card-body')) {
      return;
    }
    onClick();
  };

  const isReel = false; //trip.trip_type === 'reel';

  // Build the slides array for TripImageSlideshow.
  // Prefer cardSlides from the Phase 4 API response, then fall back to
  // previewImages / imageUrl for older API responses.
  const slides: ITripCardSlide[] = (() => {
    const cardSlidesWithUrl = (trip.cardSlides ?? []).filter((s) => s.imageUrl);
    if (cardSlidesWithUrl.length > 0) return cardSlidesWithUrl;
    if (trip.previewImages && trip.previewImages.length > 0)
      return trip.previewImages.map((url) => ({
        slideType: 'image' as const,
        imageUrl: url
      }));
    if (trip.imageUrl)
      return [{ slideType: 'image' as const, imageUrl: trip.imageUrl }];
    return [];
  })();

  function handleEditCardDisplay() {
    setShowCardEditor(true);
  }

  return (
    <Card
      data-testid='trip-card'
      className={`shadow-sm cursor-pointer trip-card h-100 ${
        isReel ? 'border-primary border-3' : ''
      }`}
      style={{ transition: 'box-shadow 0.2s' }}
      onMouseEnter={(e) => e.currentTarget.classList.add('shadow')}
      onMouseLeave={(e) => e.currentTarget.classList.remove('shadow')}
    >
      <div
        onClick={handleCardClick}
        className='position-relative bg-light'
        style={{ height: '260px' }}
      >
        {slides.length > 0 ? (
          <TripImageSlideshow
            slides={slides}
            altText={trip.title}
            onEditClick={handleEditCardDisplay}
          />
        ) : isReel && reelVideoUrl ? (
          <>
            <video
              src={reelVideoUrl}
              className='w-100 h-100'
              style={{ objectFit: 'cover' }}
              preload='metadata'
              muted
              playsInline
            />
            <div
              className='position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center'
              style={{ backgroundColor: 'rgba(0,0,0,0.2)' }}
            >
              <div className='bg-white rounded-circle p-3 shadow'>
                <Play
                  size={16}
                  className='text-dark'
                />
              </div>
            </div>
          </>
        ) : (
          <div className='w-100 h-100 d-flex align-items-center justify-content-center text-muted'>
            No image
          </div>
        )}
        {isReel && (
          <div className='position-absolute top-0 start-0 m-3'>
            <Badge
              bg='primary'
              className='rounded-pill shadow d-flex align-items-center gap-1 px-3 py-2'
              style={{
                background: 'linear-gradient(to right, #6f42c1, #d63384)',
                fontSize: '0.875rem',
                fontWeight: 'bold'
              }}
            >
              <Video size={16} />
              <span>REEL</span>
            </Badge>
          </div>
        )}
        <Button
          size='sm'
          onClick={(e) => {
            e.stopPropagation();
            setShowEditModal(true);
          }}
          variant='light'
          className='position-absolute top-0 end-0 m-3 rounded-circle p-2 shadow-sm opacity-0 trip-card-edit-btn'
          title='Edit cover image'
          style={{ transition: 'opacity 0.2s' }}
        >
          <Pencil
            size={16}
            className='text-dark'
          />
        </Button>
        <div
          className='position-absolute bottom-0 start-0 end-0 p-3'
          style={{
            background:
              'linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.4) 75%, rgba(0, 0, 0, 0.5) 100%)'
          }}
        >
          <div className='d-flex align-items-start justify-content-between gap-2'>
            <div className='flex-fill'>
              <h5
                className='text-white mb-1'
                data-testid='trip-card-title'
              >
                {capitalizeFirstLetter(trip.title)}
              </h5>
              {formattedDate && (
                <small
                  className='text-white'
                  style={{ opacity: 0.9 }}
                >
                  {formattedDate}
                </small>
              )}
            </div>
            {permissionLevel && (
              <Badge
                bg={permissionLevel === 'edit' ? 'success' : 'warning'}
                className='rounded-pill px-2 py-1 flex-shrink-0'
                style={{ fontSize: '0.75rem' }}
                title={
                  permissionLevel === 'edit'
                    ? 'You can add and edit places'
                    : 'You can only view this trip'
                }
              >
                {permissionLevel === 'edit' ? 'Can Edit' : 'View Only'}
              </Badge>
            )}
          </div>
        </div>
      </div>
      <Card.Body className='p-2'>
        <div className='d-flex align-items-center justify-content-between'>
          <div>
            {/* {trip.createdBy !== currentUserId && (
              <p className='small text-muted mb-0'>
                Shared by: <span className='fw-medium'>{trip.createdBy}</span>
              </p>
            )} */}
            <small className='text-muted'>
              Created{' '}
              {new Date(trip.createdAt).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
              })}
            </small>
          </div>
          <div className='d-flex gap-2 align-items-center'>
            {/* {trip.createdBy === currentUserId && ( */}
            <Button
              onClick={onPrivacyClick}
              disabled={togglingPrivacy}
              variant='transparent'
              height={20}
              width={20}
              className='p-0'
              style={{ opacity: togglingPrivacy ? 0.5 : 1 }}
              title={
                trip.isPublic
                  ? 'Public - Click to make private'
                  : 'Private - Click to make public'
              }
            >
              {trip.isPublic ? <Globe size={16} /> : <Lock size={16} />}
            </Button>
            {/* )} */}
            <Button
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setShowShareModal(true);
              }}
              variant='transparent'
              height={20}
              width={20}
              className='p-0'
              title='Share trip'
            >
              <Share2
                size={16}
                className='text-primary'
              />
            </Button>
            <Button
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setShowEditModal(true);
              }}
              variant='transparent'
              height={20}
              width={20}
              className='p-0'
              title='Edit trip'
            >
              <Pencil size={16} />
            </Button>
            <Button
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setShowReel(true);
              }}
              variant='transparent'
              height={20}
              width={20}
              className='p-0'
              title='View activity reel'
            >
              <Film size={16} />
            </Button>
            <Button
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setShowDeleteModal(true);
              }}
              variant='transparent'
              height={20}
              width={20}
              className='p-0'
              title='Delete trip'
            >
              <Trash2
                size={16}
                className='text-danger'
              />
            </Button>
          </div>
        </div>
      </Card.Body>

      <DeleteModal
        show={showDeleteModal}
        handleClose={() => setShowDeleteModal(false)}
        handleDelete={handleDelete}
        title={`Delete "${trip.title}"?`}
      >
        <p>
          {isCollaborator
            ? 'This will remove your access to this trip.'
            : hasCollaborators
              ? 'This trip has collaborators. Deleting it will remove access for all collaborators.'
              : 'This action cannot be undone.'}
        </p>
      </DeleteModal>

      <EditTripNameImageModal
        show={showEditModal}
        handleClose={() => setShowEditModal(false)}
        id={trip.id}
        tripTitle={trip.title}
        tripImageUrl={trip.imageUrl}
        onSuccess={() => {
          if (onUpdate) onUpdate();
        }}
      />

      <ShareInviteModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        tripId={trip.id}
        tripName={trip.title}
      />

      <MakePublicConfirmModal
        isOpen={showMakePublicModal}
        onClose={() => setShowMakePublicModal(false)}
        onSuccess={() => {
          // Refresh trip data or update UI state after successful publish
          console.log('Trip published successfully');
        }}
        tripId={trip.id}
        tripTitle={trip.title}
      />

      {showCardEditor && (
        <TripCardDisplayEditor
          tripId={trip.id}
          tripTitle={trip.title}
          tripImageUrl={trip.imageUrl || undefined}
          initialSlides={trip.cardSlides}
          initialIncludeCover={trip.isCardCustomized ? false : true}
          onClose={() => setShowCardEditor(false)}
          onSaved={() => {
            setShowCardEditor(false);
            if (onUpdate) onUpdate();
          }}
          onChangeCoverClick={() => {
            setShowCardEditor(false);
            setShowEditModal(true);
          }}
        />
      )}

      {showReel && (
        <TripReelView
          tripId={trip.id}
          cardSlides={trip.cardSlides}
          onClose={() => setShowReel(false)}
        />
      )}
    </Card>
  );
}
